import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useSmoothScroll } from "./SmoothScroll";

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="header-brand-logo" className="flex items-baseline gap-2">
    <span className={`font-display text-2xl font-extrabold tracking-tight ${dark ? "text-white" : "text-ink"}`}>
      GOOX<span className="ml-0.5 inline-block h-2 w-2 rounded-full bg-gradient-to-br from-violet-500 to-pink-500" />
    </span>
    <span className="eyebrow hidden text-[0.58rem] text-mist sm:inline">Experience Engine</span>
  </Link>
);

export const LangSwitch = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="header-language-switcher" className="pill flex items-center border border-line bg-white p-0.5 text-xs font-semibold">
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-button`}
          onClick={() => setLang(l)}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors ${lang === l ? "bg-ink text-white" : "text-ink-2 hover:text-ink"} ${compact ? "text-[10px]" : ""}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollTo } = useSmoothScroll();
  const [open, setOpen] = useState(false);

  const goAnchor = (id) => {
    setOpen(false);
    if (location.pathname === "/") scrollTo(`#${id}`);
    else navigate(`/#${id}`);
  };

  const items = [
    { id: "discover", label: t.nav.discover, to: "/discover" },
    { id: "how", label: t.nav.howItWorks, anchor: "how" },
    { id: "for-creators", label: t.nav.forCreators, anchor: "creators" },
    { id: "for-fans", label: t.footer.forFans, anchor: "fans" },
    { id: "about", label: t.nav.about, to: "/about" },
  ];

  const linkCls = (active) =>
    `pill px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "bg-violet-100 text-violet-800" : "text-ink-2 hover:bg-lilac hover:text-ink"}`;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="glass border-b border-line/70">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {items.map((it) =>
              it.to ? (
                <Link key={it.id} to={it.to} data-testid={`header-nav-${it.id}`} className={linkCls(location.pathname === it.to)}>
                  {it.label}
                </Link>
              ) : (
                <button key={it.id} data-testid={`header-nav-${it.id}`} onClick={() => goAnchor(it.anchor)} className={linkCls(false)}>
                  {it.label}
                </button>
              )
            )}
          </nav>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Link to="/login" data-testid="header-sign-in-button" className="pill hidden px-3.5 py-1.5 text-sm font-medium text-ink-2 hover:bg-lilac hover:text-ink md:inline-flex">
              {t.nav.signIn}
            </Link>
            <button data-testid="header-waitlist-cta" onClick={() => goAnchor("waitlist")} className="pill btn-primary hidden px-4 py-2 text-sm font-semibold sm:inline-flex">
              {t.nav.waitlist}
            </button>
            <button data-testid="header-mobile-menu-button" onClick={() => setOpen((v) => !v)} className="pill border border-line bg-white p-2 lg:hidden" aria-label={t.nav.menu}>
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="header-mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="glass border-b border-line px-4 py-4 lg:hidden"
          >
            <div className="flex flex-col gap-1">
              {items.map((it) =>
                it.to ? (
                  <Link key={it.id} to={it.to} onClick={() => setOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-medium text-ink hover:bg-lilac">
                    {it.label}
                  </Link>
                ) : (
                  <button key={it.id} onClick={() => goAnchor(it.anchor)} className="rounded-xl px-3 py-2.5 text-left text-sm font-medium text-ink hover:bg-lilac">
                    {it.label}
                  </button>
                )
              )}
              <Link to="/login" onClick={() => setOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-lilac">
                {t.nav.signIn}
              </Link>
              <button onClick={() => goAnchor("waitlist")} className="pill btn-primary mt-2 px-4 py-2.5 text-sm font-semibold">
                {t.nav.waitlist}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
